import { IS_NATIVE } from 'gcs-common/constants/deviceConstants';
import { DateDisplayFormat, formatDate } from 'gcs-common/design-system/utils/date-utils';
import { cn } from 'gcs-common/design-system/utils/tailwind-merge';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import TicketMessageOptions from '../TicketMessageOptions/TicketMessageOptions';

interface ForwardingOption {
  type?: string | null;
  index?: number | null;
}

interface TicketMessageBubbleProps {
  children: ReactNode;
  isFromMe: boolean;
  createdAt?: string;
  showOptions?: boolean;
  className?: string | null;
  forwardingOption?: ForwardingOption | null;
}

const TicketMessageBubble = (props: TicketMessageBubbleProps) => {
  const { children, isFromMe, createdAt, showOptions, className, forwardingOption } = props;

  const longPressTimeout = useRef<NodeJS.Timeout | null>(null);

  const [showForwarding, setShowForwarding] = useState(false);
  const shouldShowOptions = IS_NATIVE ? showForwarding : showOptions;
  const optionsClassName = !IS_NATIVE ? 'invisible group-hover:visible' : undefined;

  const handleLongPressStart = () => {
    if (!showOptions) {
      return null;
    }
    longPressTimeout.current = setTimeout(() => {
      setShowForwarding(true);
    }, 600); // 600ms para considerar long press
  };

  const handleLongPressEnd = () => {
    if (longPressTimeout.current) {
      clearTimeout(longPressTimeout.current);
    }
  };

  const innerRef = useRef(null);

  useOnClickOutside(innerRef, () => setShowForwarding(false));
  return (
    <div
      className={`flex w-full ${isFromMe ? 'justify-end' : 'justify-start'}`}
      {...(IS_NATIVE && {
        onTouchStart: handleLongPressStart,
        onTouchEnd: handleLongPressEnd,
      })}
    >
      <div
        ref={innerRef}
        className={cn(
          `group relative max-w-[50%] break-words px-3 py-2 text-neutral-12 text-sm shadow-md ring-1 ring-neutral-3 ${
            isFromMe
              ? 'rounded-t-2xl rounded-l-2xl rounded-br-sm bg-accent-4'
              : 'rounded-t-2xl rounded-r-2xl rounded-bl-sm bg-white'
          } ${className}`,
        )}
      >
        {shouldShowOptions && <TicketMessageOptions className={optionsClassName} {...forwardingOption} />}
        {children}
        {createdAt && (
          <div className="mt-1 text-right font-medium text-neutral-9 text-xs">
            {formatDate(createdAt, DateDisplayFormat.time)}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketMessageBubble;
